import React from 'react';
import { connect } from 'react-redux';
import { useLocale } from 'locale';
import { State } from 'store/state';
import * as Styled from './styled';
import { CheckedLink, getAgencyLink, getAgentLink } from 'utils/checkLinksWrapper';
import AgentLogoPlaceholder from 'assets/svg/user-placeholder/agent-new.svg';
import AgencyLogoPlaceholder from 'assets/svg/agency-empty-avatar.svg';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { HistoryHebrewItem, HistoryItemAgent, RawHistoryItemAddress } from 'store/state/selectors/insights/prices';
import { setDealAffiliationPopup } from 'store/state/app/actions';
import { AffiliationPopupMode, IDealAffiliationPopupState } from 'components/deal-affiliation-popup/types';
import { DealAffiliationType } from 'utils/entities';
import styled from '@emotion/styled';
import { TextLabel, SmallText } from 'ds/components/typography';
import CreditPromotionCard from './credit-card';
import { agentIdSelector } from 'store/state/domainData/selectors';


const CardContainer = styled.div(({ theme }) => ({
  width: '100%',
  minHeight: '172px',
  backgroundColor: theme.colors.neutrals.grey10,
  borderRadius: theme.borderRadius.small,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(3),
}));

const ContentWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
}));

const AvatarGroup = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const AvatarCircle = styled.img(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '100px',
  border: `1px solid ${theme.colors.neutrals.grey8}`,
  objectFit: 'cover',
}));

const AvatarSquare = styled.img(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: theme.borderRadius.small,
  border: `1px solid ${theme.colors.neutrals.grey8}`,
  objectFit: 'cover',
}));

const UserInfo = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  'a': {
    textDecoration: 'none',
  },
}));

const UserName = styled(TextLabel)(({ theme }) => ({
  color: theme.colors.brand.main,
  lineHeight: '24px',
}));

const AgencyName = styled(TextLabel)(({ theme }) => ({
  color: theme.colors.brand.main,
  lineHeight: '20px',
}));

const HiddenPublic = styled(SmallText)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
  margin: theme.spacing(4, 0),
}));

const RoleTag = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.neutrals.white,
  border: `1px solid ${theme.colors.neutrals.grey8}`,
  borderRadius: theme.borderRadius.small,
  padding: theme.spacing(0.25, 1),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const RoleText = styled(TextLabel)(({ theme }) => ({
  color: theme.colors.neutrals.grey3,
}));

type AssignButtonMouseEvent = React.MouseEvent<HTMLDivElement, MouseEvent> | React.MouseEvent<HTMLAnchorElement, MouseEvent>;

interface AffiliationTileProps {
  address: RawHistoryItemAddress;
  agentId: string | null;
  affiliatedAgent?: HistoryItemAgent;
  affiliationSide?: DealAffiliationType;
  item: HistoryHebrewItem;
  requested: boolean;
  openDealAffiliationPopup: (payload: IDealAffiliationPopupState) => void;
}

const AffiliationTile: React.FC<AffiliationTileProps> = ({
  address,
  agentId,
  affiliatedAgent,
  affiliationSide,
  item,
  requested,
  openDealAffiliationPopup,
}) => {
  const { t } = useLocale();
  const constructImageURI = useImageUrlBuilder();
  const role = t('widget.prices.affiliationSide', { affiliationSide });

  const handleAssignAgentButtonClick = (e: AssignButtonMouseEvent) => {
    e.stopPropagation();
    if (requested) {
      e.preventDefault();
      return;
    }

    openDealAffiliationPopup({
      isOpen: true,
      mode: AffiliationPopupMode.New,
      forceDealSide:
        affiliationSide === DealAffiliationType.Buyer || affiliationSide === DealAffiliationType.Seller
          ? affiliationSide
          : null,
      deal: {
        soldId: item.id,
        dealDate: item.date,
        price: item.price,
        structuredAddress: {
          city: address && address.city,
          streetName: address && address.stName,
          streetNumber: address && address.hNo,
          neighbourhood: address && address.neighbourhood,
        },
        rooms: item.beds,
        constructionYear: item.year,
        floor: item.floor,
        area: item.size.toString(),
      },
    });
  };

  let content = null;
  const isSameAgent = affiliatedAgent && agentId && (affiliatedAgent.agentId === agentId);
  if (isSameAgent && affiliatedAgent.hideDealsOnPublicSite) {
    return <CreditPromotionCard agentId={affiliatedAgent.agentId} />;
  }

  if (affiliatedAgent) {
    const pictureSize = 40;
    const agentName = affiliatedAgent.agentName;
    const agencyName = affiliatedAgent.agencyName;
    const logoUrl = affiliatedAgent.agencyImageUrl ? constructImageURI(affiliatedAgent.agencyImageUrl, { height: pictureSize, width: pictureSize }) : null;
    const avatarUrl = affiliatedAgent.agentImageUrl ? constructImageURI(affiliatedAgent.agentImageUrl, { height: pictureSize, width: pictureSize }) : null;

    content = affiliatedAgent.hideDealsOnPublicSite ? (
      <HiddenPublic weight="bold">
        {t('widget.prices.promotionCard.hidden.public')}
      </HiddenPublic>
    ) : (
      <>
        <AvatarGroup>
          {logoUrl ? <AvatarSquare src={logoUrl} alt={agencyName} /> : <AgencyLogoPlaceholder height={pictureSize} width={pictureSize} />}
          {avatarUrl ? <AvatarCircle src={avatarUrl} alt={agentName} /> : <AgentLogoPlaceholder height={pictureSize} width={pictureSize} />}
        </AvatarGroup>

        <UserInfo>
          {agentName ? (
            <CheckedLink link={getAgentLink(affiliatedAgent.agentId)} isPreventEvent>
              <UserName weight="bold">{agentName}</UserName>
            </CheckedLink>
          ) : null}
          {agencyName ? (
            <CheckedLink link={getAgencyLink(affiliatedAgent.agencyId)} isPreventEvent>
              <AgencyName>{agencyName}</AgencyName>
            </CheckedLink>
          ) : null}
        </UserInfo>
      </>
    );
  }
  else {
    content = (
      <>
        {requested ? <Styled.PendingRequestNote>{t('widget.prices.pendingRequestNote')}</Styled.PendingRequestNote> : null}
        <Styled.AssignAgentButton onClick={handleAssignAgentButtonClick} disabled={requested}>
          {t('widget.prices.assignDeal', { requested })}
        </Styled.AssignAgentButton>
      </>
    );
  }

  return (
    <CardContainer>
      <ContentWrapper>
        {content}
        <RoleTag>
          <RoleText>{role}</RoleText>
        </RoleTag>
      </ContentWrapper>
    </CardContainer>
  );
};

const mapStateToProps = (state: State) => ({
  agentId: agentIdSelector(state),
});

const mapDispatchToProps = {
  openDealAffiliationPopup: setDealAffiliationPopup,
};

export default connect(mapStateToProps, mapDispatchToProps)(AffiliationTile);
